import {
  Box,
  Grid,
  Toolbar,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Modal,
  Menu,
  Card,
  CardContent,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import SearchRounded from "@mui/icons-material/SearchRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import {
  CloudDownloadOutlined,
  InfoOutlined,
  Inventory2Outlined,
  PlaylistAddCheckCircleOutlined,
} from "@mui/icons-material";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import TransferStockModal from "../../../components/Inventory/TransferStockModal";

function Inventory() {
  const token = localStorage.getItem("token");

  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [stockFilter, setStockFilter] = useState("");

  const [actionQuantities, setActionQuantities] = useState({});
  const [bulkUpdateQuantities, setBulkUpdateQuantities] = useState({});
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuMaterial, setMenuMaterial] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [modalOpen, setModalOpen] = useState(false);
  const [storageLocations, setStorageLocations] = useState([]);
  const [cityStorageLocations, setCityStorageLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [currentTransferMaterial, setCurrentTransferMaterial] = useState(null);
  const [actionType, setActionType] = useState("");

  const [confirmBulkUpdateOpen, setConfirmBulkUpdateOpen] = useState(false);

  const capitalizeWords = (str = "") =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const normalize = (v) => String(v ?? "").toLowerCase().trim();

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getRawMaterialsList = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.mainCategory = selectedCategory;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock?outletId=${outletData._id}&limit=100000`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { ...params },
        }
      );

      const totalRecords = response?.data?.data?.totalRecords;
      setCategories(response?.data?.data?.rawMaterialCategories);

      if (
        typeof totalRecords === "string" &&
        totalRecords.includes("No Records")
      ) {
        setRawMaterials([]);
      } else {
        setRawMaterials(totalRecords || []);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch raw materials", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRawMaterialsList();
  }, [selectedCategory]);

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = capitalizeWords("raw material inventory");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    doc.text(title, titleX, 20);

    doc.setFontSize(10);

    const outletName = capitalizeWords(outletData?.name);
    const outletAddress = capitalizeWords(
      outletData?.address || "No Address Available"
    );
    const outletContact = `${outletData?.email || "No Email"}, ${outletData?.phone || "No Phone Info"
      }`;

    doc.text(`Outlet: ${outletName}`, 14, 30);
    doc.text(`Address: ${outletAddress}`, 14, 40);
    doc.text(`Contact: ${outletContact}`, 14, 50);

    doc.setFontSize(12);
    const startY = 60;

    const columns = [
      { header: capitalizeWords("#"), dataKey: "itemNo" },
      { header: capitalizeWords("product name"), dataKey: "productName" },
      { header: capitalizeWords("category"), dataKey: "category" },
      { header: capitalizeWords("sub category"), dataKey: "subcategory" },
      { header: capitalizeWords("stock quantity"), dataKey: "stockQuantity" },
      { header: capitalizeWords("unit"), dataKey: "unit" },
    ];

    const sortedMaterials = [...rawMaterials].sort((a, b) => {
      const categoryA =
        a?.rawMaterial?.mainCategory?.name?.toLowerCase?.() || "";
      const categoryB =
        b?.rawMaterial?.mainCategory?.name?.toLowerCase?.() || "";
      const subcategoryA =
        a?.rawMaterial?.subCategory?.name?.toLowerCase?.() || "";
      const subcategoryB =
        b?.rawMaterial?.subCategory?.name?.toLowerCase?.() || "";

      if (categoryA < categoryB) return -1;
      if (categoryA > categoryB) return 1;
      if (subcategoryA < subcategoryB) return -1;
      if (subcategoryA > subcategoryB) return 1;
      return 0;
    });

    const rows = sortedMaterials.map((material, index) => ({
      itemNo: index + 1,
      productName: capitalizeWords(material?.rawMaterial?.name || ""),
      category: capitalizeWords(material?.rawMaterial?.mainCategory?.name || ""),
      subcategory: capitalizeWords(
        material?.rawMaterial?.subCategory?.name || ""
      ),
      stockQuantity: parseFloat((material?.currentStock ?? 0).toFixed(2)),
      unit: capitalizeWords(material?.rawMaterial?.unit || ""),
    }));

    doc.autoTable({
      columns,
      body: rows,
      startY,
      theme: "grid",
      styles: { halign: "center" },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 },
      },
    });

    doc.save(`${getCurrentDate()}-Raw-Material-Inventory.pdf`);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const uniqueCategories = useMemo(() => {
    return categories?.filter(
      (category, index, self) =>
        index ===
        self.findIndex(
          (c) => c?.mainCategory?._id === category?.mainCategory?._id
        )
    );
  }, [categories]);

  const filteredMaterials = useMemo(() => {
    const list = Array.isArray(rawMaterials) ? rawMaterials : [];
    const q = normalize(searchQuery);
    const words = q ? q.split(/\s+/).filter(Boolean) : [];

    return list.filter((m) => {
      const isOut = m?.stockInfo === "Out of Stock";
      if (stockFilter === "OUT_OF_STOCK" && !isOut) return false;
      if (stockFilter === "IN_STOCK" && isOut) return false;

      if (selectedCategory) {
        const catId = m?.rawMaterial?.mainCategory?._id;
        if (catId !== selectedCategory) return false;
      }

      if (!words.length) return true;

      const haystack = normalize(
        [
          m?.rawMaterial?.name,
          m?.rawMaterial?.mainCategory?.name,
          m?.rawMaterial?.subCategory?.name,
          m?.rawMaterial?.unit,
          m?.stockInfo,
          m?.currentStock,
        ].join(" ")
      );

      return words.every((w) => haystack.includes(w));
    });
  }, [rawMaterials, searchQuery, stockFilter, selectedCategory]);

  const handleActionQuantityChange = (id, quantity) => {
    setActionQuantities((prev) => ({ ...prev, [id]: quantity }));
  };

  const handleBulkQuantityChange = (id, quantity) => {
    const value = String(quantity ?? "");

    if (value === "") {
      setBulkUpdateQuantities((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      return;
    }

    if (Number(value) < 0) return;

    setBulkUpdateQuantities((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const openMenu = (e, material) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuMaterial(material);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuMaterial(null);
  };

  const handleMenuAction = (type) => {
    closeMenu();
    setCurrentTransferMaterial(menuMaterial);
    setActionType(type);
    setModalOpen(true);
    setSelectedCity("");
    setSelectedLocation("");
  };

  const getStorageLocation = async () => {
    try {
      const response = await axios.get(`${API.BASE_URL}storage-location`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const locations = response?.data?.data?.storageLocations || [];

      const cityGroups = locations.reduce((acc, loc) => {
        if (!acc[loc.city]) acc[loc.city] = [];
        acc[loc.city].push(loc);
        return acc;
      }, {});

      setStorageLocations(locations);
      setCities(Object.keys(cityGroups));
    } catch (error) {
      if (error.response && error.response?.data?.status === false) {
        toast.error(error.response.data.error, {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
      } else {
        toast.error("Failed to fetch storage locations", { autoClose: 3000 });
      }
    }
  };

  useEffect(() => {
    getStorageLocation();
  }, []);

  const getStorageLocationCurrentStock = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}storage-location/getCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            city: selectedCity,
            rawMaterialId: currentTransferMaterial?.rawMaterial?._id,
          },
        }
      );
      setCityStorageLocations(response?.data?.data?.storageLocations || []);
    } catch (error) {
      if (error.response && error.response?.data?.status === false) {
        toast.error(error.response.data.error, {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
      } else {
        toast.error("Failed to fetch location stock", { autoClose: 3000 });
      }
    }
  };

  useEffect(() => {
    if (actionType === "add" && selectedCity) {
      getStorageLocationCurrentStock();
    }
  }, [selectedCity, actionType]);

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    setCityStorageLocations(storageLocations.filter((loc) => loc.city === city));
    setSelectedLocation("");
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedLocation("");
    setSelectedCity("");
  };

  const getRawMaterialStockCentral = async (rawMaterialId) => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getCurrentStock?rawMaterialId=${rawMaterialId}&storageLocation=${selectedLocation}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response?.data;
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch stock";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleDebitStockOutletToCentral = async (
    rawMaterialId,
    currentStockQuantity
  ) => {
    const transferQuantity = actionQuantities[rawMaterialId];

    try {
      const response = await axios.post(
        `${API.BASE_URL}stock-transactions/debit-stock-from-outlet-to-central`,
        {
          outletId: outletData._id,
          outletStockTransferData: [
            {
              rawMaterialId,
              currentStockQuantity,
              transferQuantity,
            },
          ],
          storageLocation: selectedLocation,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.status === true) {
        toast.success("Central Stock updated successful!", { autoClose: 3000 });
        getRawMaterialsList();
        setActionQuantities((prev) => ({ ...prev, [rawMaterialId]: "" }));
      }
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Transfer failed";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleAddStockCentralToOutlet = async (rawMaterialId) => {
    try {
      const response = await getRawMaterialStockCentral(rawMaterialId);
      const currentStockQuantity = response?.data?.currentStock;

      if (currentStockQuantity === null || currentStockQuantity === undefined) {
        toast.error("Unable to fetch current stock.", { autoClose: 3000 });
        return;
      }

      const transferQuantity = actionQuantities[rawMaterialId];

      const addStockResponse = await axios.post(
        `${API.BASE_URL}stock-transactions/addStockToOutlet`,
        {
          outletId: outletData._id,
          outletStockTransferData: [
            {
              rawMaterialId,
              currentStockQuantity,
              transferQuantity,
              storageLocation: selectedLocation,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (addStockResponse?.data?.status === true) {
        toast.success("Outlet Stock updated successfully!", { autoClose: 3000 });
        getRawMaterialsList();
        setActionQuantities((prev) => ({ ...prev, [rawMaterialId]: "" }));
      }
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Stock update failed";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleDebitStock = async (
    rawMaterialId,
    currentStockQuantity,
    transferQuantityArg
  ) => {
    try {
      const transferQuantity =
        transferQuantityArg !== undefined
          ? transferQuantityArg
          : actionQuantities[rawMaterialId];

      const response = await axios.post(
        `${API.BASE_URL}stock-transactions/debitStock`,
        {
          outletId: outletData._id,
          outletStockTransferData: [
            {
              rawMaterialId,
              currentStockQuantity,
              transferQuantity,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.status === true) {
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Update failed";
      toast.error(errorMessage, { autoClose: 3000 });
      return false;
    }
  };

  const handleModalConfirm = () => {
    if (!selectedLocation) {
      toast.error("Please select a storage location", { autoClose: 3000 });
      return;
    }

    const id = currentTransferMaterial?.rawMaterial?._id;
    const qty = actionQuantities[id];

    if (
      qty === "" ||
      qty === undefined ||
      qty === null ||
      Number.isNaN(Number(qty)) ||
      Number(qty) <= 0
    ) {
      toast.error("Please enter a valid quantity", { autoClose: 3000 });
      return;
    }

    if (actionType === "add") {
      handleAddStockCentralToOutlet(id);
    } else if (actionType === "debit") {
      handleDebitStockOutletToCentral(id, currentTransferMaterial?.currentStock);
    }

    handleModalClose();
  };

  const totals = useMemo(() => {
    const list = Array.isArray(rawMaterials) ? rawMaterials : [];
    const out = list.filter((m) => m?.stockInfo === "Out of Stock").length;
    return { total: list.length, out, in: Math.max(list.length - out, 0) };
  }, [rawMaterials]);

  const pendingBulkUpdates = useMemo(() => {
    const list = Array.isArray(rawMaterials) ? rawMaterials : [];

    return Object.entries(bulkUpdateQuantities)
      .map(([id, qty]) => {
        const material = list.find((item) => item?.rawMaterial?._id === id);

        if (!material) return null;

        const numericQty = Number(qty);

        if (
          qty === "" ||
          qty === undefined ||
          qty === null ||
          Number.isNaN(numericQty)
          //  ||  numericQty <= 0
        ) {
          return null;
        }

        return {
          id,
          quantity: qty,
          numericQty,
          currentStock: material?.currentStock,
          name: material?.rawMaterial?.name,
          unit: material?.rawMaterial?.unit,
          mainCategory: material?.rawMaterial?.mainCategory?.name,
          subCategory: material?.rawMaterial?.subCategory?.name,
          material,
        };
      })
      .filter(Boolean);
  }, [bulkUpdateQuantities, rawMaterials]);

  const hasPendingUpdates = pendingBulkUpdates.length > 0;

  const handleRemovePendingUpdate = (id) => {
    setBulkUpdateQuantities((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleClearAllPendingUpdates = () => {
    setBulkUpdateQuantities({});
  };

  const handleBulkUpdateAll = async () => {
    if (!pendingBulkUpdates.length) {
      toast.error("Please enter at least one valid quantity", {
        autoClose: 3000,
      });
      return;
    }

    try {
      setBulkUpdating(true);

      let successCount = 0;

      for (const item of pendingBulkUpdates) {
        const ok = await handleDebitStock(
          item.id,
          item.currentStock,
          item.numericQty
        );
        if (ok) successCount += 1;
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} raw material${successCount > 1 ? "s" : ""
          } updated successfully!`,
          { autoClose: 3000 }
        );
      }

      if (successCount === pendingBulkUpdates.length) {
        setBulkUpdateQuantities({});
      }

      await getRawMaterialsList();
    } finally {
      setBulkUpdating(false);
      setConfirmBulkUpdateOpen(false);
    }
  };

  return (
    <Box
      className="dashBoard"
      sx={{
        background:
          "linear-gradient(180deg, #f6f8fb 0%, #eef4fb 100%)",
        minHeight: "100vh",
      }}
    >
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11.5}>
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                    Raw Materials Inventory
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Search, filter, update in-stock quantity, or use ⋮ for Get / Transfer.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Chip
                    label={`Total: ${totals.total}`}
                    variant="outlined"
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                  />
                  <Chip
                    label={`In Stock: ${totals.in}`}
                    variant="outlined"
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                  />
                  <Chip
                    label={`Out of Stock: ${totals.out}`}
                    variant="outlined"
                    sx={{
                      fontWeight: 900,
                      borderRadius: 2,
                      borderColor: "#fecaca",
                      color: "#991b1b",
                      background: "#fff1f2",
                    }}
                  />

                  <CloudDownloadOutlined
                    onClick={generatePDF}
                    sx={{ fontSize: 34, cursor: "pointer", color: "#003049" }}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>


          <Card
            elevation={0}
            sx={{
              mt: 1.5,
              borderRadius: 4,
              border: "1px solid #e6eaf0",
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
            }}
          >
            <CardContent sx={{ py: 2.2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    Search Raw Materials
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by material, category, unit..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(String(e.target.value))}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        height: 54,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    Category
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        height: 54,
                        background: "#fff",
                      },
                    }}
                  >
                    <Select
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {uniqueCategories?.map((category) => (
                        <MenuItem
                          key={category?.mainCategory?._id}
                          value={category?.mainCategory?._id}
                        >
                          {category?.mainCategory?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: "#334155",
                      mb: 0.8,
                    }}
                  >
                    Stock Status
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        height: 54,
                        background: "#fff",
                      },
                    }}
                  >
                    <Select
                      value={stockFilter}
                      onChange={(e) => setStockFilter(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="IN_STOCK">In Stock</MenuItem>
                      <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Tooltip
                    title="Enter update quantity on the left. Added items will automatically appear on the right panel. Use ⋮ for transfer actions."
                    arrow
                  >
                    <IconButton
                      sx={{
                        border: "1px solid #dbe4ef",
                        background: "#fff",
                      }}
                    >
                      <InfoOutlined />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: hasPendingUpdates ? "9fr 4fr" : "1fr 0fr",
              gap: hasPendingUpdates ? 2 : 0,
              alignItems: "stretch",
              transition:
                "grid-template-columns 350ms ease, gap 350ms ease",
            }}
          >
            {/* LEFT */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: "1px solid #e6eaf0",
                overflow: "hidden",
                background: "#fff",
                height: "76vh",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
              }}
            >
              <CardContent
                sx={{
                  p: 0,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      py: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress />
                    <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                      Fetching Raw Materials...
                    </Typography>
                  </Box>
                ) : filteredMaterials?.length === 0 ? (
                  <Box sx={{ py: 8, textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: "#0f172a",
                      }}
                    >
                      No raw materials to display
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.7,
                        fontSize: 13,
                        color: "#64748b",
                      }}
                    >
                      Try changing the search, category, or stock filter.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                      }}
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: 0,
                          height: "100%",
                          maxHeight: "100%",
                          overflow: "auto",
                          "&::-webkit-scrollbar": {
                            width: 8,
                            height: 8,
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#cbd5e1",
                            borderRadius: 20,
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f8fafc",
                          },
                        }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              {["No.", "Product", "In Stock", "Update Qty", "Actions"].map(
                                (h) => (
                                  <TableCell
                                    key={h}
                                    sx={{
                                      fontWeight: 900,
                                      color: "#0f172a",
                                      background: "#f4f8fc",
                                      borderBottom: "1px solid #e2e8f0",
                                      whiteSpace: "nowrap",
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 2,
                                      py: 1.8,
                                    }}
                                    align={h === "Product" ? "left" : "center"}
                                  >
                                    {h}
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {filteredMaterials.map((material, index) => {
                              const displayIndex = index + 1;
                              const id = material?.rawMaterial?._id;
                              const enteredQty = bulkUpdateQuantities[id] ?? "";
                              const isOutOfStock = material?.stockInfo === "Out of Stock";
                              const isSelectedForBulk =
                                enteredQty !== "" &&
                                enteredQty !== undefined &&
                                enteredQty !== null &&
                                !Number.isNaN(Number(enteredQty)) &&
                                Number(enteredQty) > 0;

                              return (
                                <TableRow
                                  key={id || index}
                                  hover
                                  sx={{
                                    transition: "all 180ms ease",
                                    backgroundColor: isSelectedForBulk
                                      ? "#eff7ff"
                                      : isOutOfStock
                                        ? "#fff7f8"
                                        : index % 2 === 0
                                          ? "#ffffff"
                                          : "#fbfdff",
                                    "&:hover": {
                                      backgroundColor: isSelectedForBulk
                                        ? "#e3f1ff"
                                        : "#f8fbff",
                                    },
                                    "& td": {
                                      borderBottom: "1px solid #f1f5f9",
                                    },
                                  }}
                                >
                                  <TableCell
                                    align="center"
                                    sx={{ fontWeight: 700, color: "#334155" }}
                                  >
                                    {displayIndex}
                                  </TableCell>

                                  <TableCell align="left" sx={{ minWidth: 180 }}>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                    >
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                            color: "#0f172a",
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {material?.rawMaterial?.name || "N/A"}
                                        </Typography>

                                        <Typography
                                          sx={{
                                            fontSize: 10.5,
                                            color: "#64748b",
                                            textTransform: "capitalize",
                                            mt: 0.3,
                                          }}
                                        >
                                          {material?.rawMaterial?.mainCategory?.name || "N/A"}
                                        </Typography>

                                        <Typography
                                          sx={{
                                            fontSize: 10,
                                            color: "#475569",
                                            mt: 0.4,
                                          }}
                                        >
                                          <b>Unit:</b> {material?.rawMaterial?.unit || "N/A"}
                                        </Typography>
                                      </Box>

                                      {isOutOfStock && (
                                        <Chip
                                          label="Out of Stock"
                                          size="small"
                                          sx={{
                                            background: "#ffe4e6",
                                            color: "#9f1239",
                                            fontWeight: 900,
                                            fontSize: 10,
                                            borderRadius: 2,
                                          }}
                                          variant="outlined"
                                        />
                                      )}

                                      {isSelectedForBulk && (
                                        <Chip
                                          label="Added"
                                          size="small"
                                          sx={{
                                            background: "#dbeafe",
                                            color: "#1d4ed8",
                                            fontWeight: 900,
                                            borderRadius: 2,
                                          }}
                                          variant="outlined"
                                        />
                                      )}
                                    </Stack>
                                  </TableCell>

                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: 800,
                                      color: "#0f172a",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {Number(material?.currentStock || 0).toFixed(2)}
                                  </TableCell>

                                  <TableCell align="center">
                                    <TextField
                                      // type="number"
                                      inputProps={{ min: 0 }}
                                      value={enteredQty}
                                      onChange={(e) =>
                                        handleBulkQuantityChange(id, e.target.value)
                                      }
                                      size="small"
                                      placeholder="Enter qty"
                                      sx={{
                                        width: 110,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2.5,
                                          background: "#fff",
                                        },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell align="center">
                                    <Tooltip title="More actions (Get Stock / Transfer)">
                                      <span>
                                        <IconButton
                                          onClick={(e) => openMenu(e, material)}
                                          sx={{
                                            border: "1px solid #e6eaf0",
                                            borderRadius: 2.5,
                                            background: "#fff",
                                            "&:hover": {
                                              background: "#f8fbff",
                                            },
                                          }}
                                        >
                                          <MoreVertIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* RIGHT */}
            <Box
              sx={{
                minWidth: 0,
                overflow: "hidden",
                opacity: hasPendingUpdates ? 1 : 0,
                transform: hasPendingUpdates
                  ? "translateX(0)"
                  : "translateX(18px)",
                pointerEvents: hasPendingUpdates ? "auto" : "none",
                transition:
                  "opacity 300ms ease, transform 350ms ease",
              }}
            >
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                  height: "76vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                }}
              >
                <CardContent
                  sx={{
                    p: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      px: 2.2,
                      py: 2,
                      borderBottom: "1px solid #e6eaf0",
                      background:
                        "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: "#0f172a",
                      }}
                    >
                      Pending Updates
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 13,
                        color: "#64748b",
                      }}
                    >
                      Everything added here will be updated together.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 2.2,
                      py: 1.5,
                      borderBottom: "1px solid #eef2f7",
                      background: "#fafcff",
                      flexShrink: 0,
                    }}
                  >
                    <Chip
                      label={`Items Added: ${pendingBulkUpdates.length}`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2.5 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      p: 1.5,
                      "&::-webkit-scrollbar": {
                        width: 8,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#cbd5e1",
                        borderRadius: 20,
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f8fafc",
                      },
                    }}
                  >
                    <List sx={{ p: 0 }}>
                      {pendingBulkUpdates.map((item) => (
                        <Card
                          key={item.id}
                          elevation={0}
                          sx={{
                            mb: 1.3,
                            borderRadius: 3,
                            border: "1px solid #e6eef7",
                            background:
                              "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                            boxShadow: "0 6px 14px rgba(15, 23, 42, 0.03)",
                          }}
                        >
                          <ListItem
                            sx={{
                              py: 1.4,
                              px: 1.6,
                              alignItems: "flex-start",
                            }}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                onClick={() => handleRemovePendingUpdate(item.id)}
                                sx={{
                                  mt: 0.5,
                                  border: "1px solid #e2e8f0",
                                  borderRadius: 2.5,
                                  background: "#fff",
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    textTransform: "capitalize",
                                    pr: 4,
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ mt: 0.8, pr: 4 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#64748b",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {item.mainCategory || "N/A"}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#475569",
                                      mt: 0.7,
                                    }}
                                  >
                                    <b>Current:</b>{" "}
                                    {Number(item.currentStock || 0).toFixed(2)}{" "}
                                    {item.unit}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      display: "inline-flex",
                                      mt: 1,
                                      px: 1.2,
                                      py: 0.5,
                                      borderRadius: 2,
                                      fontSize: 12,
                                      fontWeight: 900,
                                      color: "#003049",
                                      background: "#eaf4ff",
                                      border: "1px solid #cfe5ff",
                                    }}
                                  >
                                    Update Qty: {item.quantity} {item.unit}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        </Card>
                      ))}
                    </List>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderTop: "1px solid #e6eaf0",
                      background: "#fafcff",
                      flexShrink: 0,
                    }}
                  >
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleClearAllPendingUpdates}
                        disabled={!pendingBulkUpdates.length || bulkUpdating}
                        sx={{
                          height: 48,
                          borderRadius: 2.5,
                          fontWeight: 900,
                          textTransform: "none",
                        }}
                      >
                        Clear All
                      </Button>

                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!pendingBulkUpdates.length || bulkUpdating}
                        onClick={() => setConfirmBulkUpdateOpen(true)}
                        sx={{
                          height: 48,
                          borderRadius: 2.5,
                          fontWeight: 900,
                          textTransform: "none",
                          background:
                            "linear-gradient(135deg, #003049 0%, #014f77 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #002437 0%, #01415e 100%)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#b0bec5",
                            color: "#ffffff",
                          },
                        }}
                      >
                        {bulkUpdating ? "Updating..." : "Update All"}
                      </Button>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={closeMenu}>
        <MenuItem onClick={() => handleMenuAction("add")}>
          Get Stock (from Storage)
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("debit")}>
          Transfer to Storage
        </MenuItem>
      </Menu>

      <TransferStockModal
        open={modalOpen}
        onClose={handleModalClose}
        data={{
          actionType,
          actionQuantities,
          currentTransferMaterial,
          selectedCity,
          cities,
          selectedLocation,
          cityStorageLocations,
        }}
        handlers={{
          onQuantityChange: handleActionQuantityChange,
          onCityChange: handleCityChange,
          onLocationChange: setSelectedLocation,
          onConfirm: handleModalConfirm,
        }}
      />

      <Modal
        open={confirmBulkUpdateOpen}
        onClose={() => setConfirmBulkUpdateOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 520,
            maxWidth: "92vw",
            bgcolor: "background.paper",
            borderRadius: 4,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
            border: "1px solid #e2e8f0",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 900, color: "#003049" }}
          >
            Confirm Bulk Update
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: "#334155" }}>
            Are you sure you want to update{" "}
            <strong>{pendingBulkUpdates.length}</strong> raw material
            {pendingBulkUpdates.length > 1 ? "s" : ""} together?
          </Typography>

          <Box
            sx={{
              maxHeight: 220,
              overflow: "auto",
              textAlign: "left",
              border: "1px solid #e2e8f0",
              borderRadius: 2.5,
              mb: 3,
              p: 1,
              background: "#f8fafc",
            }}
          >
            {pendingBulkUpdates.map((item) => (
              <Box
                key={item.id}
                sx={{
                  px: 1.2,
                  py: 1,
                  borderBottom: "1px solid #e2e8f0",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    textTransform: "capitalize",
                  }}
                >
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#475569" }}>
                  Current: {Number(item.currentStock || 0).toFixed(2)} {item.unit}{" "}
                  | Update Qty: {item.quantity} {item.unit}
                </Typography>
              </Box>
            ))}
          </Box>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setConfirmBulkUpdateOpen(false)}
              sx={{
                flex: 1,
                height: 48,
                borderColor: "#d32f2f",
                color: "#d32f2f",
                borderRadius: 2.5,
                fontWeight: 900,
                "&:hover": {
                  backgroundColor: "#fdecea",
                  borderColor: "#c62828",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleBulkUpdateAll}
              disabled={bulkUpdating}
              sx={{
                flex: 1,
                height: 48,
                background:
                  "linear-gradient(135deg, #003049 0%, #014f77 100%)",
                borderRadius: 2.5,
                fontWeight: 900,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #002437 0%, #01415e 100%)",
                },
              }}
            >
              {bulkUpdating ? "Updating..." : "Confirm"}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
}

export default Inventory;